import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit'
import {getCookie, setCookie} from '../../utils/cookies'

import {
  accessControlActionCookieName,
  ALLOW_TOKEN,
  DENY_TOKEN,
  INIT_VALUE,
  PENDING_VALUE,
  TOKEN_EXPIRE_MAPPER
} from './constants'
import {getAccessControlRule} from './getAccessControlRule'
import {
  AccessControlState,
  AccessControlAction,
  AccessControlRule,
  FetchAccessControlParams
} from '../types'
import getHours from 'date-fns/getHours'
import getDay from 'date-fns/getDay'
import {trackAccessControlList} from './trackAccessControlList'

/**
 * Generates an array of number the size it receives
 * @param x - array number
 * @example
 * ```js
 * const array = createArrayOfXPositions(3)
 * console.log(array) // [1, 2, 3]
 * ```
 */
export const createArrayOfXPositions = (x: number) =>
  Array.from({length: x}, (_, index: number) => `${index + 1}`)

/**
 * Basic mapper to be used pre fetchAccessControl call
 */
export const mapAccessControlParams = (
  {
    userId,
    emailDomain,
    anonymousId,
    deviceId,
    deviceType,
    userIpAddress,
    userCountryCode,
    isUserIpAnonymous,
    trafficSource,
    gsite,
    dayDistance,
    currencyCode,
    localeCode,
    hotelId,
    hotelCountry
  },
  algoliaClient
): FetchAccessControlParams => ({
  payload: {
    user: {
      id: userId,
      emailDomain,
      anonymousId
    },
    device: {
      id: deviceId,
      type: deviceType
    },
    ip: {
      address: userIpAddress,
      country: userCountryCode,
      isAnonymous: isUserIpAnonymous
    },
    urlParameters: {
      utm_source: trafficSource,
      gsite
    },
    search: {
      dayDistance,
      currency: currencyCode,
      language: localeCode,
      dayHour: getHours(new Date()),
      weekDay: getDay(new Date())
    },
    rate: {
      hotelId,
      hotelCountry,
      tier: 'private'
    },
    optionalFiltersScoreOffset: createArrayOfXPositions(18)
  },
  algoliaClient
})

/**
 * Fetch both access control rules (ALLOW and DENY).
 * A short circuit is implemented in which if the ALLOW is matched
 * it avoids fetching DENY.
 * @param params - user payload and algolia client
 */
export const fetchAccessControl = createAsyncThunk(
  'fetchAccessControl',
  async (params: FetchAccessControlParams, {dispatch}) => {
    const {rule: allowRule, hits: allowHits} = await getAccessControlRule(
      params,
      ALLOW_TOKEN
    )

    // Only call DENY rules in case there's no match with ALLOW rule
    const {rule: denyRule, hits: denyHits} =
      !allowRule && (await getAccessControlRule(params, DENY_TOKEN))

    trackAccessControlList(
      params.payload,
      allowHits || denyHits,
      allowRule || denyRule,
      dispatch
    )

    return allowRule || denyRule
  }
)

/**
 * Access control slice initial state
 */
export const initialState: AccessControlState = {
  action:
    (getCookie(accessControlActionCookieName) as AccessControlAction) ||
    INIT_VALUE
}

/**
 * Store access control action to cookies in case of ALLOW/DENY values.
 */
const updateAccessControlCookie = (ruleAction: AccessControlAction) => {
  if ([ALLOW_TOKEN, DENY_TOKEN].includes(ruleAction)) {
    setCookie(accessControlActionCookieName, ruleAction, {
      expires: TOKEN_EXPIRE_MAPPER[ruleAction]
    })
  }
}

/**
 * Access control slice.
 * Holds both access control action code and rule id
 */
const {actions, reducer} = createSlice({
  name: 'accessControl',
  initialState,
  reducers: {
    updateAccessControl: (
      state,
      {payload}: PayloadAction<AccessControlRule>
    ) => {
      const ruleAction = payload?.action

      state.action = ruleAction
      state.ruleId = payload?.ruleId

      updateAccessControlCookie(ruleAction)
    }
  },
  extraReducers: {
    [fetchAccessControl.pending.type]: state => {
      state.action = PENDING_VALUE
    },
    [fetchAccessControl.fulfilled.type]: (
      state,
      {payload}: PayloadAction<AccessControlRule>
    ) => {
      const ruleAction = payload?.action

      state.action = ruleAction
      state.ruleId = payload?.ruleId

      updateAccessControlCookie(ruleAction)
    },
    [fetchAccessControl.rejected.type]: state => {
      console.error('Error requesting ACL API')
      state.action = null
      state.ruleId = null
    }
  }
})

export const {updateAccessControl} = actions

export default reducer
